import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DesignatePlacementsModal } from '@/components/DesignatePlacementsModal';
import { toast } from 'sonner';
import { 
  Shield, 
  Edit, 
  Lock, 
  Unlock, 
  Archive, 
  Trash2,
  Plus,
  Minus,
  Save,
  Settings,
  Crown,
  Users,
  MapPin,
  CalendarDays,
  Globe,
  Trophy
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPanel = ({ tournament, member, participants, onUpdate, onDelete }) => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editScores, setEditScores] = useState({});
  const [saving, setSaving] = useState(false);
  const [designateOpen, setDesignateOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState(false);
  const [tournamentForm, setTournamentForm] = useState({
    name: '',
    description: '',
    num_holes: 3,
    holes: [],
    tournament_type: 'free',
    venue_name: '',
    venue_address: '',
    venue_maps_link: '',
    event_date: '',
    tour_id: '',
    season_id: ''
  });

  const [tours, setTours] = useState([]);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/tours`).then(res => setTours(res.data)).catch(() => {});
    axios.get(`${API_URL}/seasons`).then(res => setSeasons(res.data)).catch(() => {});
  }, []);

  if (!member?.is_admin) return null;

  const initTournamentEdit = () => {
    // Convert ISO date to datetime-local format for the input
    let eventDateLocal = '';
    if (tournament.event_date) {
      const d = new Date(tournament.event_date);
      eventDateLocal = d.getFullYear() + '-' + 
        String(d.getMonth() + 1).padStart(2, '0') + '-' + 
        String(d.getDate()).padStart(2, '0') + 'T' + 
        String(d.getHours()).padStart(2, '0') + ':' + 
        String(d.getMinutes()).padStart(2, '0');
    }
    setTournamentForm({
      name: tournament.name,
      description: tournament.description || '',
      num_holes: tournament.num_holes,
      holes: tournament.holes.map(h => ({ ...h })),
      tournament_type: tournament.tournament_type || 'free',
      venue_name: tournament.venue_name || '',
      venue_address: tournament.venue_address || '',
      venue_maps_link: tournament.venue_maps_link || '',
      event_date: eventDateLocal,
      tour_id: tournament.tour_id || '',
      season_id: tournament.season_id || ''
    });
    setEditingTournament(true);
  };

  const updateHolePar = (holeNumber, delta) => {
    setTournamentForm(prev => ({
      ...prev,
      holes: prev.holes.map(h => 
        h.hole_number === holeNumber 
          ? { ...h, par: Math.max(1, Math.min(6, h.par + delta)) }
          : h
      )
    }));
  };

  const handleNumHolesChange = (newNumHoles) => {
    const num = parseInt(newNumHoles);
    const newHoles = [];
    for (let i = 1; i <= num; i++) {
      const existing = tournamentForm.holes.find(h => h.hole_number === i);
      newHoles.push(existing || { hole_number: i, par: 3 });
    }
    setTournamentForm(prev => ({
      ...prev,
      num_holes: num,
      holes: newHoles
    }));
  };

  const handleSaveTournament = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/tournaments/${tournament.id}?admin_id=${member.id}`,
        {
          name: tournamentForm.name,
          description: tournamentForm.description,
          num_holes: tournamentForm.num_holes,
          holes: tournamentForm.holes,
          tournament_type: tournamentForm.tournament_type,
          venue_name: tournamentForm.venue_name || null,
          venue_address: tournamentForm.venue_address || null,
          venue_maps_link: tournamentForm.venue_maps_link || null,
          event_date: tournamentForm.event_date ? new Date(tournamentForm.event_date).toISOString() : null,
          tour_id: tournamentForm.tour_id || null,
          season_id: tournamentForm.season_id || null
        }
      );
      toast.success('Tournament updated successfully');
      setEditingTournament(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tournament');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTournament = async () => {
    if (!window.confirm(`Are you sure you want to delete "${tournament.name}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/admin/tournaments/${tournament.id}?admin_id=${member.id}`
      );
      toast.success('Tournament deleted successfully');
      if (onDelete) onDelete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tournament');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(
        `${API_URL}/admin/tournaments/${tournament.id}/status?status=${newStatus}&admin_id=${member.id}`
      );
      toast.success(`Tournament status changed to ${newStatus}`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleEditPlayer = (player) => {
    setSelectedPlayer(player);
    const scoreMap = {};
    player.hole_scores?.forEach(hs => {
      scoreMap[hs.hole_number] = hs.strokes;
    });
    // Initialize with tournament par if no scores
    if (Object.keys(scoreMap).length === 0) {
      tournament.holes.forEach(h => {
        scoreMap[h.hole_number] = h.par;
      });
    }
    setEditScores(scoreMap);
  };

  const updateEditScore = (holeNumber, delta) => {
    setEditScores(prev => ({
      ...prev,
      [holeNumber]: Math.max(1, (prev[holeNumber] || 0) + delta)
    }));
  };

  const handleSaveScores = async () => {
    if (!selectedPlayer) return;
    
    setSaving(true);
    try {
      const holeScores = Object.entries(editScores).map(([hole, strokes]) => ({
        hole_number: parseInt(hole),
        strokes: strokes
      }));
      
      await axios.put(
        `${API_URL}/admin/tournaments/${tournament.id}/scores/${selectedPlayer.member_id}?admin_id=${member.id}`,
        { hole_scores: holeScores }
      );
      
      toast.success(`Scores updated for ${selectedPlayer.member_name}`);
      setSelectedPlayer(null);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update scores');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePlayer = async (playerId, playerName) => {
    if (!confirm(`Remove ${playerName} from this tournament?`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/admin/tournaments/${tournament.id}/participants/${playerId}?admin_id=${member.id}`
      );
      toast.success(`${playerName} removed from tournament`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove player');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-500/20 text-green-500 border-green-500';
      case 'in_progress': return 'bg-purple-500/20 text-purple-500 border-purple-500';
      case 'closed': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500';
      case 'completed': return 'bg-blue-500/20 text-blue-500 border-blue-500';
      case 'archived': return 'bg-gray-500/20 text-gray-500 border-gray-500';
      default: return 'bg-gray-500/20 text-gray-500 border-gray-500';
    }
  };

  return (
    <Card className="bg-card border-primary/30 mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center justify-between text-primary">
          <span className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Admin Controls
          </span>
          <Dialog open={editingTournament} onOpenChange={setEditingTournament}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={initTournamentEdit}
                className="border-primary/50 hover:bg-primary/20"
                data-testid="admin-edit-tournament"
              >
                <Settings className="w-4 h-4 mr-1" />
                Edit Tournament
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Edit Tournament</DialogTitle>
                <DialogDescription>
                  Modify tournament name, description, holes, and par values
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 my-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Tournament Name</label>
                  <Input
                    value={tournamentForm.name}
                    onChange={(e) => setTournamentForm({ ...tournamentForm, name: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Description</label>
                  <Input
                    value={tournamentForm.description}
                    onChange={(e) => setTournamentForm({ ...tournamentForm, description: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Number of Holes</label>
                  <Select 
                    value={tournamentForm.num_holes.toString()} 
                    onValueChange={handleNumHolesChange}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="3">3 Holes</SelectItem>
                      <SelectItem value="6">6 Holes</SelectItem>
                      <SelectItem value="9">9 Holes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Tournament Type</label>
                  <Select 
                    value={tournamentForm.tournament_type} 
                    onValueChange={(value) => setTournamentForm({ ...tournamentForm, tournament_type: value })}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="free">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span>Free / Amateur</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="pro">
                        <div className="flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-400" />
                          <span>Paid / Pro Tour</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {tournamentForm.tournament_type === 'pro' 
                      ? 'Only Pro members can join this tournament' 
                      : 'Open to all members (Amateur & Pro)'}
                  </p>
                </div>

                {/* Event Schedule Section */}
                <div className="border-t border-white/10 pt-4">
                  <label className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    Event Schedule
                  </label>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Event Date & Time</label>
                      <Input
                        type="datetime-local"
                        value={tournamentForm.event_date}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, event_date: e.target.value })}
                        className="bg-muted border-border"
                        data-testid="admin-event-date-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Venue Name</label>
                      <Input
                        placeholder="e.g., Pine Valley Golf Club"
                        value={tournamentForm.venue_name}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, venue_name: e.target.value })}
                        className="bg-muted border-border"
                        data-testid="admin-venue-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Address</label>
                      <Input
                        placeholder="e.g., 123 Main St, City, State"
                        value={tournamentForm.venue_address}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, venue_address: e.target.value })}
                        className="bg-muted border-border"
                        data-testid="admin-venue-address-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Google Maps Link</label>
                      <Input
                        placeholder="https://maps.google.com/..."
                        value={tournamentForm.venue_maps_link}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, venue_maps_link: e.target.value })}
                        className="bg-muted border-border"
                        data-testid="admin-venue-maps-link-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Tour Assignment */}
                {tours.length > 0 && (
                  <div className="border-t border-white/10 pt-4">
                    <label className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Tour Assignment
                    </label>
                    <Select
                      value={tournamentForm.tour_id || 'none'}
                      onValueChange={(value) => setTournamentForm({ ...tournamentForm, tour_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className="bg-muted border-border" data-testid="admin-tour-select">
                        <SelectValue placeholder="No tour assigned" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        <SelectItem value="none">
                          <span className="text-muted-foreground">No tour assigned</span>
                        </SelectItem>
                        {tours.map(tour => (
                          <SelectItem key={tour.id} value={tour.id}>
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-primary" />
                              <span>{tour.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Season Assignment */}
                {seasons.length > 0 && (
                  <div className="border-t border-white/10 pt-4">
                    <label className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      Season Assignment
                    </label>
                    <Select
                      value={tournamentForm.season_id || 'none'}
                      onValueChange={(value) => setTournamentForm({ ...tournamentForm, season_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className="bg-muted border-border" data-testid="admin-season-select">
                        <SelectValue placeholder="No season assigned" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        <SelectItem value="none">
                          <span className="text-muted-foreground">No season assigned</span>
                        </SelectItem>
                        {seasons.map(season => (
                          <SelectItem key={season.id} value={season.id}>
                            <span>{season.name} {season.is_active ? '(Active)' : ''}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-muted-foreground">Hole Par Values</label>
                    <span className="text-sm text-primary font-mono">
                      Total Par: {tournamentForm.holes.reduce((sum, h) => sum + h.par, 0)}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tournamentForm.holes.map((hole) => (
                      <div 
                        key={hole.hole_number}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <span className="text-white">Hole {hole.hole_number}</span>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateHolePar(hole.hole_number, -1)}
                            disabled={hole.par <= 1}
                            className="h-8 w-8 rounded-full score-btn-minus"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-mono text-lg text-white">
                            {hole.par}
                          </span>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateHolePar(hole.hole_number, 1)}
                            disabled={hole.par >= 6}
                            className="h-8 w-8 rounded-full score-btn-plus"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSaveTournament}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tournament Status Control */}
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm text-muted-foreground">Tournament Status:</span>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={tournament.status === 'open' ? 'default' : 'outline'}
              onClick={() => handleStatusChange('open')}
              className={tournament.status === 'open' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="admin-status-open"
            >
              <Unlock className="w-4 h-4 mr-1" />
              Open
            </Button>
            <Button
              size="sm"
              variant={tournament.status === 'in_progress' ? 'default' : 'outline'}
              onClick={() => handleStatusChange('in_progress')}
              className={tournament.status === 'in_progress' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              data-testid="admin-status-in-progress"
            >
              In Progress
            </Button>
            <Button
              size="sm"
              variant={tournament.status === 'closed' ? 'default' : 'outline'}
              onClick={() => handleStatusChange('closed')}
              className={tournament.status === 'closed' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
              data-testid="admin-status-closed"
            >
              <Lock className="w-4 h-4 mr-1" />
              Closed
            </Button>
            <Button
              size="sm"
              variant={tournament.status === 'completed' ? 'default' : 'outline'}
              onClick={() => handleStatusChange('completed')}
              className={tournament.status === 'completed' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid="admin-status-completed"
            >
              Completed
            </Button>
            {tournament.status === 'completed' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDesignateOpen(true)}
                className="border-yellow-500/50 hover:bg-yellow-500/20 text-yellow-300"
                data-testid="open-designate-placements"
              >
                <Trophy className="w-4 h-4 mr-1" />
                Designate Winners
              </Button>
            )}
            <Button
              size="sm"
              variant={tournament.status === 'archived' ? 'default' : 'outline'}
              onClick={() => handleStatusChange('archived')}
              className={tournament.status === 'archived' ? 'bg-gray-600 hover:bg-gray-700' : ''}
              data-testid="admin-status-archived"
            >
              <Archive className="w-4 h-4 mr-1" />
              Archive
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDeleteTournament}
              className="border-destructive/50 hover:bg-destructive/20 text-destructive"
              data-testid="admin-delete-tournament"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>

        {/* Player Score Management */}
        <div className="border-t border-white/10 pt-4">
          <h4 className="text-sm font-medium text-white mb-3">Edit Player Scores</h4>
          <div className="grid gap-2 max-h-60 overflow-y-auto">
            {participants.map((player) => (
              <div 
                key={player.member_id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg"
              >
                <div>
                  <span className="text-white font-medium">{player.member_name}</span>
                  <span className="text-muted-foreground text-sm ml-2">{player.member_number}</span>
                </div>
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditPlayer(player)}
                        className="border-primary/50 hover:bg-primary/20"
                        data-testid={`admin-edit-${player.member_id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-white/10">
                      <DialogHeader>
                        <DialogTitle className="font-heading">
                          Edit Scores: {selectedPlayer?.member_name}
                        </DialogTitle>
                        <DialogDescription>
                          Modify the scores for each hole
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-3 my-4 max-h-80 overflow-y-auto">
                        {tournament.holes.map((hole) => (
                          <div 
                            key={hole.hole_number}
                            className="flex items-center justify-between p-3 bg-muted rounded-lg"
                          >
                            <span className="text-white">
                              Hole {hole.hole_number} 
                              <span className="text-muted-foreground text-sm ml-2">Par {hole.par}</span>
                            </span>
                            <div className="flex items-center gap-2">
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => updateEditScore(hole.hole_number, -1)}
                                disabled={(editScores[hole.hole_number] || 0) <= 1}
                                className="h-8 w-8 rounded-full score-btn-minus"
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-8 text-center font-mono text-lg text-white">
                                {editScores[hole.hole_number] || hole.par}
                              </span>
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => updateEditScore(hole.hole_number, 1)}
                                className="h-8 w-8 rounded-full score-btn-plus"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button 
                          onClick={handleSaveScores}
                          disabled={saving}
                          className="bg-primary hover:bg-primary/90"
                          data-testid="admin-save-scores"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          {saving ? 'Saving...' : 'Save Scores'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemovePlayer(player.member_id, player.member_name)}
                    className="border-destructive/50 hover:bg-destructive/20 text-destructive"
                    data-testid={`admin-remove-${player.member_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {participants.length === 0 && (
              <p className="text-muted-foreground text-center py-4">No participants yet</p>
            )}
          </div>
        </div>
      </CardContent>

      <DesignatePlacementsModal
        open={designateOpen}
        onOpenChange={setDesignateOpen}
        tournament={tournament}
        adminId={member?.id}
        onSuccess={onUpdate}
      />
    </Card>
  );
};
